import { Phone, Shield, Check, Clock, ClipboardList, TestTube, Stethoscope, Heart, Activity, MessageCircle, Settings } from 'lucide-react';

const WHATSAPP_LINK = "https://wa.me/5541988451399?text=Olá,%20gostaria%20de%20agendar%20uma%20avaliação%20médica.";

export const ComoFuncionaPage = () => {
  return (
    <main data-testid="como-funciona-page" className="pt-24">
      {/* Hero */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-[0.2em] text-[#B8944D] mb-6">
              Dr. Eduardo Hadas — CRM-PR 40929
            </p>
            
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Aqui você não recebe apenas uma prescrição.
            </h1>
            
            <p className="text-2xl text-[#B8944D] mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              Você recebe acompanhamento médico real.
            </p>
            
            <p className="text-lg text-[#666666] leading-relaxed max-w-2xl">
              Emagrecimento clínico e reposição hormonal conduzidos como processos médicos contínuos, 
              com individualização total, reavaliações periódicas e presença médica constante.
            </p>
          </div>
        </div>
      </section>

      {/* Por que meu modelo é diferente */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Por que meu modelo de acompanhamento é diferente.
            </h2>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                A maioria das pessoas está acostumada com um modelo médico pontual:
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                Consulta rápida, prescrição e retorno apenas se algo der errado.
              </p>
              
              <p>
                Esse modelo falha em emagrecimento e reposição hormonal.
              </p>
              
              <p>
                Porque esses tratamentos não são eventos isolados. São processos médicos que exigem 
                acompanhamento contínuo, ajustes e reavaliações.
              </p>
              
              <p className="text-xl text-[#B8944D]" style={{ fontFamily: 'var(--font-heading)' }}>
                Aqui, o tratamento não termina na consulta. Ele começa nela.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visão geral do processo */}
      <section className="section-padding bg-[#E5E2DD]/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="section-title mb-12 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Uma visão clara do processo.
            </h2>
            
            <div className="flex flex-wrap justify-center gap-4">
              {[
                'Avaliação médica aprofundada',
                'Exames laboratoriais',
                'Definição da estratégia',
                'Início do tratamento',
                'Acompanhamento contínuo',
                'Reavaliações e ajustes',
                'Manutenção e otimização',
              ].map((step, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 bg-white px-6 py-3 rounded-full border border-[#E5E2DD]"
                >
                  <span className="text-[#B8944D] font-medium">{index + 1}</span>
                  <span className="text-[#2A2A2A] text-sm">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Etapas detalhadas */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto space-y-16">
            {/* Etapa 1 */}
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="step-number">1</div>
                <div className="w-px h-full bg-[#E5E2DD] mt-4" />
              </div>
              <div className="pb-8">
                <h3 
                  className="text-2xl text-[#2A2A2A] mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Etapa 1 — Avaliação médica aprofundada
                </h3>
                <p className="text-[#666666] leading-relaxed mb-4">
                  Iniciamos com uma consulta detalhada para entender:
                </p>
                <ul className="space-y-2 text-[#666666]">
                  {['Sintomas', 'Queixas físicas, emocionais e sociais', 'Histórico clínico', 'Tentativas anteriores', 'Rotina real', 'Funcionamento hormonal', 'Expectativas e objetivos'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B8944D]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-[#2A2A2A] font-medium mt-4">
                  Aqui não existe pressa. Entender o paciente é parte essencial do tratamento.
                </p>
              </div>
            </div>

            {/* Etapa 2 */}
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="step-number">2</div>
                <div className="w-px h-full bg-[#E5E2DD] mt-4" />
              </div>
              <div className="pb-8">
                <h3 
                  className="text-2xl text-[#2A2A2A] mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Etapa 2 — Avaliação laboratorial completa
                </h3>
                <p className="text-[#666666] leading-relaxed mb-4">
                  Solicitamos exames para entender como o organismo está funcionando antes de qualquer intervenção. 
                  Esses exames nos mostram:
                </p>
                <ul className="space-y-2 text-[#666666]">
                  {['Estado hormonal', 'Metabolismo', 'Marcadores inflamatórios', 'Deficiências nutricionais', 'Saúde hepática e renal'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B8944D]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-[#2A2A2A] font-medium mt-4">
                  Todas as decisões seguintes são guiadas por esses dados.
                </p>
              </div>
            </div>

            {/* Etapa 3 */}
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="step-number">3</div>
                <div className="w-px h-full bg-[#E5E2DD] mt-4" />
              </div>
              <div className="pb-8">
                <h3 
                  className="text-2xl text-[#2A2A2A] mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Etapa 3 — Estratégia terapêutica individualizada
                </h3>
                <p className="text-[#666666] leading-relaxed mb-4">
                  Com base na história clínica e nos exames, definimos:
                </p>
                <ul className="space-y-2 text-[#666666]">
                  {['Se o caso é de emagrecimento clínico', 'Se há necessidade de suporte hormonal', 'Se existe indicação de reposição hormonal', 'Se é possível reativar o eixo hormonal'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B8944D]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-[#2A2A2A] font-medium mt-4">
                  Cada paciente recebe um plano construído especificamente para sua realidade. 
                  Não existem protocolos prontos. Não existe dose padrão.
                </p>
              </div>
            </div>

            {/* Etapa 4 */}
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="step-number">4</div>
                <div className="w-px h-full bg-[#E5E2DD] mt-4" />
              </div>
              <div className="pb-8">
                <h3 
                  className="text-2xl text-[#2A2A2A] mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Etapa 4 — Início do tratamento
                </h3>
                <p className="text-[#666666] leading-relaxed mb-4">
                  Após definição da estratégia, iniciamos o tratamento com:
                </p>
                <ul className="space-y-2 text-[#666666]">
                  {['Orientações alimentares individualizadas', 'Medicações (se indicadas)', 'Suporte hormonal (se necessário)', 'Terapias complementares (quando aplicável)'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B8944D]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-[#2A2A2A] font-medium mt-4">
                  Tudo é explicado com clareza. Você sabe exatamente o que está usando, por que está usando e qual é o objetivo.
                </p>
              </div>
            </div>

            {/* Etapa 5 */}
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="step-number">5</div>
                <div className="w-px h-full bg-[#E5E2DD] mt-4" />
              </div>
              <div className="pb-8">
                <h3 
                  className="text-2xl text-[#2A2A2A] mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Etapa 5 — Acompanhamento contínuo
                </h3>
                <p className="text-[#666666] leading-relaxed mb-4">
                  O acompanhamento não termina após a consulta.
                </p>
                <p className="text-[#2A2A2A] font-medium mb-4">
                  Todos os meus pacientes em tratamento têm acesso direto a mim.
                </p>
                <p className="text-[#666666] leading-relaxed mb-4">
                  Isso permite:
                </p>
                <ul className="space-y-2 text-[#666666]">
                  {['Ajustes rápidos de dose', 'Correção de efeitos colaterais', 'Dúvidas em tempo real', 'Prevenção de erros'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B8944D]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-[#B8944D] font-medium mt-4">
                  Esse nível de proximidade é o que torna o tratamento seguro e eficaz.
                </p>
              </div>
            </div>

            {/* Etapa 6 */}
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="step-number">6</div>
                <div className="w-px h-full bg-[#E5E2DD] mt-4" />
              </div>
              <div className="pb-8">
                <h3 
                  className="text-2xl text-[#2A2A2A] mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Etapa 6 — Reavaliações e ajustes
                </h3>
                <p className="text-[#666666] leading-relaxed mb-4">
                  Nenhum organismo responde da mesma forma. Por isso, realizamos reavaliações periódicas, que podem ser:
                </p>
                <ul className="space-y-2 text-[#666666]">
                  {['Mensais', 'Trimestrais', 'Semestrais', 'Ou em intervalos mais longos'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B8944D]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-[#2A2A2A] font-medium mt-4">
                  Ajustes de dose, correções de estratégia e prevenção de efeitos adversos fazem parte do tratamento. 
                  Isso não é falha. É sinal de acompanhamento médico sério.
                </p>
              </div>
            </div>

            {/* Etapa 7 */}
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="step-number">7</div>
              </div>
              <div>
                <h3 
                  className="text-2xl text-[#2A2A2A] mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Etapa 7 — Manutenção e otimização
                </h3>
                <p className="text-[#666666] leading-relaxed mb-4">
                  Após atingir estabilidade metabólica e hormonal, entramos na fase de manutenção. Aqui o foco é:
                </p>
                <ul className="space-y-2 text-[#666666]">
                  {['Manter os resultados', 'Prevenir efeito rebote', 'Otimizar bem-estar', 'Reduzir doses quando possível', 'Garantir segurança a longo prazo'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B8944D]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-[#2A2A2A] font-medium mt-4">
                  Em muitos casos, esse acompanhamento segue ao longo da vida.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* O que está incluso */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="section-title mb-12 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O que está incluso no acompanhamento.
            </h2>
            
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { icon: ClipboardList, text: 'Consulta médica aprofundada' },
                { icon: TestTube, text: 'Avaliação laboratorial' },
                { icon: Stethoscope, text: 'Estratégia terapêutica individual' },
                { icon: Heart, text: 'Prescrição personalizada' },
                { icon: Activity, text: 'Acompanhamento contínuo' },
                { icon: Clock, text: 'Reavaliações periódicas' },
                { icon: Settings, text: 'Ajustes de dose' },
                { icon: MessageCircle, text: 'Comunicação direta com o médico' },
                { icon: Shield, text: 'Prevenção de efeitos colaterais' },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 bg-white p-4 rounded-md border border-[#E5E2DD]">
                  <item.icon className="w-5 h-5 text-[#B8944D] flex-shrink-0" />
                  <span className="text-[#2A2A2A] text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Segurança, ética e responsabilidade */}
      <section className="section-padding bg-[#B68A5A]/10">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-8">
              <Shield className="w-8 h-8 text-[#B8944D]" />
            </div>
            
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Segurança, ética e responsabilidade.
            </h2>
            
            <div className="space-y-4 text-lg text-[#666666] leading-relaxed">
              <p className="text-[#2A2A2A] font-medium">
                Emagrecimento e reposição hormonal não são brincadeira.
              </p>
              
              <p>
                Todo tratamento é conduzido com base em:
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 my-6">
                {['Exames', 'Critérios clínicos', 'Ciência médica', 'Acompanhamento contínuo'].map((item, index) => (
                  <span key={index} className="bg-white px-4 py-2 rounded-full text-[#B8944D] text-sm font-medium">
                    {item}
                  </span>
                ))}
              </div>
              
              <p>
                Evitamos promessas, excessos e soluções genéricas.
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                Meu compromisso é com sua saúde — não com resultados rápidos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-[#2A2A2A] text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl text-white mb-6 leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Se você quer um tratamento médico sério,{' '}
              <span className="text-[#B8944D]">vamos conversar</span>.
            </h2>
            
            <p className="text-lg text-white/70 mb-10">
              Agende uma avaliação médica e entenda se o meu acompanhamento faz sentido para o seu caso.
            </p>
            
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="como-funciona-cta-button"
              className="btn-primary inline-flex items-center gap-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              Agendar avaliação médica
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
